import { useMemo } from "react";
import type { IFeatures } from "@/utils/interfaces";
import type { LegacyPageConfig } from "./legacyPageRegistryTypes";
import { useLegacyPageContext, useLegacyPageRegistry } from "./useLegacyPageRegistry";

interface LegacyNavigationModel {
  activePages: IFeatures[];
  sideBarItens: IFeatures[];
  avatarMenuItens: IFeatures[];
  initialPage: IFeatures | undefined;
}

/**
 * Modelo de navegação do legado derivado do `pageRegistry`.
 *
 * Objetivo:
 * - centralizar a fonte de verdade de menu/visibilidade em um único lugar
 * - continuar servindo `IFeatures[]` enquanto componentes legados ainda dependem disso
 */
export function useLegacyNavigationModel(): LegacyNavigationModel {
  const registry = useLegacyPageRegistry();
  const context = useLegacyPageContext();

  return useMemo(() => {
    const allConfigs: LegacyPageConfig[] = Object.values(registry);

    const enabledFeatures: IFeatures[] = allConfigs
      .filter((config) => config.meta?.isEnabled?.(context) ?? true)
      .map((config) => config.meta?.legacyFeature)
      .filter(Boolean) as IFeatures[];

    const initialPage = enabledFeatures.find((feature) => feature.initial);

    const avatarMenuItens: IFeatures[] = enabledFeatures
      .filter((feature) => feature.avatarMenu)
      .sort((a: any, b: any) => (a?.avatarIndex ?? 0) - (b?.avatarIndex ?? 0));

    const sideBarItens: IFeatures[] = enabledFeatures
      .filter((feature) => feature.sideMenu)
      .sort(
        (a: any, b: any) => (a?.sideMenuIndex ?? 0) - (b?.sideMenuIndex ?? 0),
      );

    return {
      activePages: enabledFeatures,
      sideBarItens,
      avatarMenuItens,
      initialPage,
    };
  }, [context, registry]);
}

